import { FormattedMessage } from 'react-intl';
import { z } from 'zod';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormElement } from '@/components/FormElement.tsx';
import { InputText } from '@/components/Inputs/InputText.tsx';
import Button from '@/components/core/Button.tsx';
import { IconPlus } from '@/icons';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { toast } from '@/hooks/useToast';
import {
  getListTenantUsersQueryKey,
  inviteTenantUserBody,
  useInviteTenantUser,
  useListTenantUsers,
} from '@/api/trafikportalen';
import { FormSection } from '../FormSection';
import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { trimData } from '@/helpers/trimData';

type AddSystemAdministratorType = z.infer<typeof inviteTenantUserBody>;

export const AddSystemAdministratorDialog = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const [ref] = useAutoAnimateConditional<HTMLFormElement>();
  const customErrorMap = useCustomErrorMap;
  const inviteTenantUser = useInviteTenantUser();
  const queryClient = useQueryClient();
  const { data: administrators, isLoading } = useListTenantUsers();

  const methods = useForm<AddSystemAdministratorType>({
    defaultValues: {
      user_type: 'admin',
    },
    resolver: zodResolver(inviteTenantUserBody, {
      errorMap: customErrorMap(),
    }),
    mode: 'all',
  });

  const watchEmail = methods.watch('email');

  const isEmailAlreadyInUse = useMemo(() => {
    return administrators?.some(
      (admin) => admin.email?.toLowerCase() === watchEmail?.toLowerCase(),
    );
  }, [administrators, watchEmail]);

  const onSubmit: SubmitHandler<AddSystemAdministratorType> = useCallback(
    (data) => {
      const submitData = trimData({ ...data, email: data.email.toLowerCase() });

      inviteTenantUser.mutate(
        { data: submitData },
        {
          onSuccess: async () => {
            onClose();
            await queryClient.invalidateQueries({
              queryKey: getListTenantUsersQueryKey(),
            });
            toast.success(
              {
                title: `Systemadministratören ${data.first_name} ${data.last_name} lades till`,
              },
              { autoClose: 3000 },
            );
          },
          onError: (error) => {
            console.error('Failed to add system administrator:', error);
            toast.error(
              {
                title: 'Kunde inte lägga till systemadministratören',
              },
              { autoClose: 3000 },
            );
          },
        },
      );
    },
    [inviteTenantUser, onClose, queryClient],
  );

  const isSubmitDisabled = useMemo(() => {
    if (inviteTenantUser.isPending) {
      return true;
    }

    if (isEmailAlreadyInUse) {
      return true;
    }

    if (isLoading) {
      return true;
    }

    if (!methods.formState.isDirty) {
      return true;
    }

    if (!methods.formState.isValid) {
      return true;
    }

    return false;
  }, [
    inviteTenantUser.isPending,
    isEmailAlreadyInUse,
    isLoading,
    methods.formState.isDirty,
    methods.formState.isValid,
  ]);

  return (
    <FormProvider {...methods}>
      <form ref={ref} onSubmit={methods.handleSubmit(onSubmit)}>
        <FormSection>
          <FormElement
            id="email"
            label="E-postadress"
            required
            input={
              <InputText
                name="email"
                control={methods.control}
                editMode={true}
                error={
                  isEmailAlreadyInUse
                    ? {
                        message: 'E-postadressen används redan',
                        type: 'custom',
                      }
                    : methods.formState.errors.email
                }
                helperTextReserveSpace
                required
              />
            }
          />
          <FormElement
            id="first_name"
            label="Förnamn"
            required
            input={
              <InputText
                name="first_name"
                control={methods.control}
                editMode={true}
                error={methods.formState.errors.first_name}
                helperTextReserveSpace
                required
              />
            }
          />
          <FormElement
            id="last_name"
            label="Efternamn"
            required
            input={
              <InputText
                name="last_name"
                control={methods.control}
                editMode={true}
                error={methods.formState.errors.last_name}
                helperTextReserveSpace
                required
              />
            }
          />
          <i className="text-text-800">
            <FormattedMessage defaultMessage="Detta sätter bara den initiala informationen. Efter detta är det enbart användaren själv som kan redigera sin information." />
          </i>
          <Button
            startIcon={<IconPlus />}
            variant="filled"
            type="submit"
            className="w-full"
            disabled={isSubmitDisabled}
            loading={inviteTenantUser.isPending}
          >
            <FormattedMessage defaultMessage="Lägg till systemadministratör" />
          </Button>
        </FormSection>
      </form>
    </FormProvider>
  );
};
